/**
 * It is assumed that the `PREFETCH_STORAGE_KEY` session storage information expires after `EXPIRATION_MINUTES`
 * If the session storage information for `PREFETCH_STORAGE_KEY` is not expired, then, generate prefetch links using cached resources
 * Else, use the api /flights-search-api/resources, to get the resources
 */

import { ClientLogger } from "bernie-client";
import { ExperimentStore } from "bernie-core";
import { SystemEvent, SystemEventLevel } from "bernie-logger";

const EXPIRATION_MINUTES = 10;
export const PREFETCH_STORAGE_KEY = "flights-search-prefetch-resources";

export interface Resource {
  readonly path: string;
  readonly type: string;
}

let linksAdded = false;

export const generatePrefetchLinks = (resource: Resource): void => {
  const { path } = resource;
  const linkElement = document.createElement("link");
  linkElement.rel = "prefetch";
  linkElement.href = path;
  //For future usage, `resource.type` could be used for adding type specific attributes eg: link.as = "style" later
  document.head.appendChild(linkElement);
};

export const isSessionStorageExist = () => window.hasOwnProperty("sessionStorage") && window.sessionStorage;

export const setResourcesInSessionStorage = (resources: Resource[]): void => {
  if (isSessionStorageExist()) {
    sessionStorage.setItem(
      PREFETCH_STORAGE_KEY,
      JSON.stringify({
        timestamp: new Date(),
        resources,
      })
    );
  }
};

export const isSessionStorageOutdated = (timestamp: Date): boolean => {
  const expiration = new Date(timestamp);
  const now = new Date();
  expiration.setMinutes(expiration.getMinutes() + EXPIRATION_MINUTES);

  return now.getTime() > expiration.getTime();
};

export const removeSessionStorage = (): void => {
  sessionStorage.removeItem(PREFETCH_STORAGE_KEY);
};

export const getValidResourcesFromSessionStorage = (): Resource[] | undefined => {
  if (isSessionStorageExist()) {
    const prefetchCacheData = sessionStorage.getItem(PREFETCH_STORAGE_KEY);
    if (prefetchCacheData) {
      const parsedPrefetchCacheData = JSON.parse(prefetchCacheData);
      const { timestamp, resources } = parsedPrefetchCacheData;
      if (isSessionStorageOutdated(timestamp)) {
        removeSessionStorage();

        return;
      }

      return resources;
    }

    return;
  }

  return;
};

export const processResources = (resources: Resource[]): void => {
  for (const resource of resources) {
    generatePrefetchLinks(resource);
  }
  linksAdded = true;
};

export const processPrefetch = async (experiment: ExperimentStore | undefined) => {
  // do not append prefetch links once appended on the current screen
  const isFSRPrefetchingEnabled = experiment?.exposures.FSR_STOREFRONT_PREFETCH.bucket;
  if (linksAdded || !isFSRPrefetchingEnabled) {
    return;
  }
  const logger = ClientLogger.getLoggerWithIdentifier("processFlightsSearchPrefetch");
  try {
    const cacheResources = getValidResourcesFromSessionStorage();
    if (cacheResources) {
      processResources(cacheResources);
    } else {
      const url = `${window.location.origin}/flights-search-api/resources?v=${new Date().getTime()}`;
      const response = await fetch(url);
      if (response.ok) {
        const resources = await response.json();
        setResourcesInSessionStorage(resources);

        processResources(resources);
      }
    }
  } catch (error) {
    logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, "ENVERROR"), error);
  }
};
